import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle, Plus, Trash2 } from 'lucide-react';
import {
  Customer,
  updateCustomer,
  getCustomer,
  CustomerTag,
  getCustomerTags,
  createCustomerTag,
  deleteCustomerTag,
  updateCustomerTags,
  CustomerAdditionalAddress,
  parseCustomerAdditionalAddresses,
  sanitizeCustomerAdditionalAddressesForSave,
} from '../../lib/customers';
import CustomerAdditionalAddressesFields from '../customer/CustomerAdditionalAddressesFields';
import AddressAutocompleteModal from '../AddressAutocompleteModal';
import { useBusiness } from '../../contexts/BusinessContext';

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
  onError?: (error: string) => void;
  customer: Customer | null;
}

export default function EditCustomerModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  customer
}: EditCustomerModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    type: 'person' | 'business';
    first_name: string;
    last_name: string;
    company: string;
    email: string;
    phone: string;
    address: string;
    lat?: number | null;
    lng?: number | null;
  }>({
    type: 'person',
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
  });
  const [additionalAddresses, setAdditionalAddresses] = useState<CustomerAdditionalAddress[]>([]);
  
  // Tags state
  const [customerTags, setCustomerTags] = useState<CustomerTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [assignedTagIds, setAssignedTagIds] = useState<string[]>([]);
  const [initialTagIds, setInitialTagIds] = useState<string[]>([]);
  const tagsDropdownRef = useRef<HTMLDivElement>(null);

  // Load customer tags
  const loadCustomerTags = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoadingTags(true);
      const tags = await getCustomerTags(selectedBusiness.id);
      setCustomerTags(tags);
    } catch (err) {
      console.error('Error loading customer tags:', err);
    } finally {
      setLoadingTags(false);
    }
  };

  // Initialize assigned tags from customer
  useEffect(() => {
    if (customer?.Tags) {
      const tagIds = Array.isArray(customer.Tags) 
        ? customer.Tags.map((tag: any) => typeof tag === 'string' ? tag : tag.id)
        : [];
      setAssignedTagIds(tagIds);
      setInitialTagIds(tagIds);
    } else {
      setAssignedTagIds([]);
      setInitialTagIds([]);
    }
  }, [customer?.Tags]);

  // Check if tags have changed
  const hasTagChanges = React.useMemo(() => {
    if (assignedTagIds.length !== initialTagIds.length) return true;
    const sortedCurrent = [...assignedTagIds].sort();
    const sortedInitial = [...initialTagIds].sort();
    return JSON.stringify(sortedCurrent) !== JSON.stringify(sortedInitial);
  }, [assignedTagIds, initialTagIds]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
        setIsTagsDropdownOpen(false);
      }
    };

    if (isTagsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTagsDropdownOpen]);

  useEffect(() => {
    if (isOpen && customer) {
      setIsAnimating(true);
      setError(null);

      // Load fresh customer data
      const loadCustomer = async () => {
        try {
          if (!customer?.id) {
            throw new Error('Invalid customer ID');
          }

          const freshCustomer = await getCustomer(customer.id);

          setFormData({
            type: freshCustomer.type ?? 'person',
            first_name: freshCustomer.first_name ?? '',
            last_name: freshCustomer.last_name ?? '',
            company: freshCustomer.company ?? '',
            email: freshCustomer.email ?? '',
            phone: freshCustomer.phone ?? '',
            address: freshCustomer.address ?? '',
            lat: freshCustomer.lat ?? null,
            lng: freshCustomer.lng ?? null,
          });
          setAdditionalAddresses(parseCustomerAdditionalAddresses(freshCustomer.additional_addresses));
          
          // Load tags
          if (selectedBusiness?.id) {
            await loadCustomerTags();
          }
          
          setError(null);
        } catch (err) {
          console.error('Error loading customer:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to load customer details';
          setError(errorMessage);
          if (onError) {
            onError(errorMessage);
          }
        }
      };
      loadCustomer();
    }
  }, [isOpen, customer, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleCreateTag = async () => {
    if (!selectedBusiness?.id || !newTagName.trim()) return;

    try {
      setIsCreatingTag(true);
      const newTag = await createCustomerTag(selectedBusiness.id, newTagName, newTagColor);
      setCustomerTags(prevTags => {
        const updatedTags = [...prevTags, newTag].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        return updatedTags;
      });
      setNewTagName('');
      setNewTagColor('#3B82F6');
    } catch (err) {
      console.error('Error creating tag:', err);
      setError(err instanceof Error ? err.message : 'Failed to create tag');
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!window.confirm('Are you sure you want to delete this tag? This will remove it from all customers.')) return;

    try {
      setLoading(true);
      await deleteCustomerTag(tagId);
      setCustomerTags(customerTags.filter(tag => tag.id !== tagId));
      setAssignedTagIds(assignedTagIds.filter(id => id !== tagId));
    } catch (err) {
      console.error('Error deleting tag:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete tag');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer || !customer.id) {
      setError('Invalid customer ID');
      if (onError) {
        onError('Invalid customer ID');
      }
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.first_name?.trim()) {
        throw new Error('First name is required');
      }
      if (!formData.last_name?.trim()) {
        throw new Error('Last name is required');
      }
      if (!formData.phone?.trim()) {
        throw new Error('Phone number is required');
      }

      const additional_addresses = sanitizeCustomerAdditionalAddressesForSave(additionalAddresses);

      const updatedCustomer = await updateCustomer(customer.id, {
        ...formData,
        company: formData.company.trim() || null,
        additional_addresses,
      });
      
      // Update tags if they have changed
      if (hasTagChanges) {
        await updateCustomerTags(customer.id, assignedTagIds);
        // Reload customer to get updated tags
        const customerWithTags = await getCustomer(customer.id);
        onSuccess(customerWithTags);
      } else {
        onSuccess(updatedCustomer);
      }
    } catch (err) {
      console.error('Error updating customer:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer details';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!customer) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Edit Customer
            </h2>
          </div>

          {/* Form Content */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
              paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
            }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Customer Information */}
              <div className="bg-white rounded-xl border border-gray-300">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                  <h3 className="text-base font-semibold text-gray-900">Customer Information</h3>
                </div>
                <div className="p-4" style={{ position: 'relative', zIndex: 1 }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Enter first name"
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Enter last name"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company
                        <span className="text-xs text-gray-500 ml-1 font-normal">Shown on invoices</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Company / business name (optional)"
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        placeholder="Enter email address (optional)"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address
                        <span className="text-xs text-gray-500 ml-1 font-normal">Google autocomplete enabled</span>
                      </label>
                      <AddressAutocompleteModal
                        value={formData.address}
                        onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                        onSelect={(address) => setFormData(prev => ({ ...prev, address: address.formatted, lat: address.lat, lng: address.lng }))}
                        placeholder="Enter address (optional)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      />
                    </div>

                    <CustomerAdditionalAddressesFields
                      modalAutocomplete
                      entries={additionalAddresses}
                      onChange={setAdditionalAddresses}
                    />
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              <div className="bg-white rounded-xl border border-gray-300 overflow-hidden" style={{ overflow: 'visible' }}>
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center relative">
                  <h3 className="text-base font-semibold text-gray-900">Tags</h3>
                  <div className="relative" ref={tagsDropdownRef} style={{ zIndex: 1000 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTagsDropdownOpen(!isTagsDropdownOpen);
                      }}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add Tags
                    </button>
                    {isTagsDropdownOpen && createPortal(
                      <>
                        <div 
                          className="fixed inset-0"
                          style={{ zIndex: 100011 }}
                          onMouseDown={(e) => {
                            if (e.target === e.currentTarget) {
                              setIsTagsDropdownOpen(false);
                            }
                          }}
                        />
                        <div 
                          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl w-80 max-w-[90vw] max-h-[80vh] overflow-y-auto"
                          style={{
                            zIndex: 100012,
                            ...(() => {
                              const isMobile = window.innerWidth < 768;
                              if (isMobile) {
                                return {
                                  left: '50%',
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                };
                              } else {
                                return {
                                  top: tagsDropdownRef.current ? 
                                    `${tagsDropdownRef.current.getBoundingClientRect().bottom + 8}px` : '50%',
                                  right: tagsDropdownRef.current ? 
                                    `${window.innerWidth - tagsDropdownRef.current.getBoundingClientRect().right}px` : '50%',
                                };
                              }
                            })()
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <div className="p-4 border-b border-gray-200" onClick={(e) => e.stopPropagation()}>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">Create New Tag</h4>
                            <div className="space-y-3">
                              <input
                                type="text"
                                placeholder="Tag name"
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter' && newTagName.trim()) {
                                    e.preventDefault();
                                    handleCreateTag();
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={newTagColor}
                                  onChange={(e) => setNewTagColor(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                                />
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCreateTag();
                                  }}
                                  disabled={!newTagName.trim() || isCreatingTag}
                                  className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  {isCreatingTag ? 'Creating...' : 'Create Tag'}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="p-4" onClick={(e) => e.stopPropagation()}>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">Select Tags</h4>
                            {loadingTags ? (
                              <div className="text-center py-4 text-sm text-gray-500">Loading tags...</div>
                            ) : customerTags.length === 0 ? (
                              <div className="text-center py-4 text-sm text-gray-500">No tags available. Create one above.</div>
                            ) : (
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {customerTags.map((tag) => (
                                  <div 
                                    key={tag.id} 
                                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <div className="flex items-center gap-2 flex-1">
                                      <input
                                        type="checkbox"
                                        checked={assignedTagIds.includes(tag.id)}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          if (e.target.checked) {
                                            setAssignedTagIds([...assignedTagIds, tag.id]);
                                          } else {
                                            setAssignedTagIds(assignedTagIds.filter(id => id !== tag.id));
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <div
                                        className="w-4 h-4 rounded-full"
                                        style={{ backgroundColor: tag.color || '#3B82F6' }}
                                      />
                                      <span className="text-sm text-gray-900">{tag.name}</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTag(tag.id);
                                      }}
                                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                      title="Delete tag"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsTagsDropdownOpen(false);
                                  setNewTagName('');
                                  setNewTagColor('#3B82F6');
                                }}
                                className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {(() => {
                    const tagsToDisplay = customer?.Tags || [];
                    return tagsToDisplay && Array.isArray(tagsToDisplay) && tagsToDisplay.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {tagsToDisplay.map((tag: any, index: number) => {
                          const tagData = typeof tag === 'object' && tag !== null 
                            ? tag 
                            : customerTags.find(t => t.id === tag);
                          
                          if (!tagData) return null;
                          
                          const tagName = typeof tagData === 'object' ? tagData.name : customerTags.find(t => t.id === tagData)?.name || '';
                          const tagColor = typeof tagData === 'object' ? (tagData.color || '#3B82F6') : (customerTags.find(t => t.id === tagData)?.color || '#3B82F6');
                          
                          return (
                            <span
                              key={typeof tagData === 'object' ? tagData.id : tagData || index}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                              style={{
                                backgroundColor: `${tagColor}20`,
                                color: tagColor,
                                border: `1px solid ${tagColor}40`
                              }}
                            >
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: tagColor }}
                              />
                              {tagName}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No tags assigned</div>
                    );
                  })()}
                </div>
              </div>

            </div>

            {/* Submit Button */}
            <div className="bg-white p-6">
              <button
                type="submit"
                disabled={loading}
                className={`w-full inline-flex items-center justify-center px-6 py-3.5 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                  loading ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Saving Changes...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
                     </form>
         </div>
       </div>
     </div>
   );

  return createPortal(modalContent, document.body);
 }